import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

// ─── Types ─────────────────────────────────────────────────────────

export interface GiftSuggestion {
    title: string;
    imageUrl: string;
    productUrl: string;
    priceRange: 'budget' | 'mid' | 'premium';
    occasions: string[];
}

export interface RecipientCategory {
    id: string;
    label: string;
    emoji: string;
    suggestions: GiftSuggestion[];
}

export interface Occasion {
    id: string;
    label: string;
    emoji: string;
}

export interface Budget {
    id: string;
    label: string;
    emoji: string;
    range: string;
}

export interface TemplatesData {
    CATEGORIES: RecipientCategory[];
    OCCASIONS: Occasion[];
    BUDGETS: Budget[];
}

// ─── Component ──────────────────────────────────────────────────────

type Step = 'who' | 'occasion' | 'budget' | 'results';

export default function Generator() {
    useSEO(
        'Kreator Prezentów — PickOneGift',
        'Nie wiesz co kupić? Odpowiedz na 3 pytania, a my zaproponujemy idealny zestaw prezentów!'
    );

    const [data, setData] = useState<TemplatesData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [step, setStep] = useState<Step>('who');
    const [selectedCategory, setSelectedCategory] = useState<RecipientCategory | null>(null);
    const [selectedOccasion, setSelectedOccasion] = useState<string>('');
    const [selectedBudget, setSelectedBudget] = useState<string>('');

    // Fetch dynamic JSON from S3 storage (public folder via CloudFront)
    useEffect(() => {
        fetch('/data/templates.json')
            .then(res => {
                if (!res.ok) throw new Error('Nie udało się pobrać bazy prezentów');
                return res.json();
            })
            .then(json => {
                setData(json);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError('Problem z połączeniem z bazą prezentów.');
                setIsLoading(false);
            });
    }, []);

    const filteredSuggestions = useMemo(() => {
        if (!selectedCategory) return [];
        return selectedCategory.suggestions.filter((s: GiftSuggestion) => {
            const matchesOccasion = selectedOccasion === 'any' || s.occasions.includes(selectedOccasion);
            const matchesBudget = s.priceRange === selectedBudget;
            return matchesOccasion && matchesBudget;
        });
    }, [selectedCategory, selectedOccasion, selectedBudget]);

    // If no exact matches, show all from the category in that budget range
    const displaySuggestions = useMemo(() => {
        if (filteredSuggestions.length > 0) return filteredSuggestions;
        if (!selectedCategory) return [];
        return selectedCategory.suggestions.filter((s: GiftSuggestion) => s.priceRange === selectedBudget);
    }, [filteredSuggestions, selectedCategory, selectedBudget]);

    const stepIndex = step === 'who' ? 0 : step === 'occasion' ? 1 : step === 'budget' ? 2 : 3;

    const handleCategorySelect = (cat: RecipientCategory) => {
        setSelectedCategory(cat);
        setStep('occasion');
    };

    const handleOccasionSelect = (occasionId: string) => {
        setSelectedOccasion(occasionId);
        setStep('budget');
    };

    const handleBudgetSelect = (budgetRange: string) => {
        setSelectedBudget(budgetRange);
        setStep('results');
    };

    const reset = () => {
        setStep('who');
        setSelectedCategory(null);
        setSelectedOccasion('');
        setSelectedBudget('');
    };

    if (isLoading) {
        return (
            <div className="page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="spinner" style={{ width: '48px', height: '48px', borderWidth: '4px' }}></div>
                    <p className="text-muted mt-md">Łączenie z bazą pomysłów AI...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <h2>Błąd pobierania</h2>
                    <p className="text-muted">{error}</p>
                </div>
                <Footer />
            </div>
        );
    }

    const { CATEGORIES, OCCASIONS, BUDGETS } = data;

    return (
        <div className="page">
            <Header />

            {/* Hero */}
            <div style={{
                textAlign: 'center',
                padding: '48px 24px 24px',
                background: 'linear-gradient(180deg, rgba(124, 92, 252, 0.1) 0%, transparent 100%)',
            }}>
                <h1 style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
                    🪄 <span style={{ background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Kreator Prezentów</span>
                </h1>
                <p className="text-muted" style={{ fontSize: '1.05rem', maxWidth: '550px', margin: '0 auto' }}>
                    Nie wiesz co kupić? Odpowiedz na 3 proste pytania, a my zaproponujemy idealne pomysły na prezent z naszej inteligentnej bazy.
                </p>
            </div>

            <div className="container" style={{ flex: 1, padding: '24px 24px 80px', maxWidth: '900px' }}>

                {/* Progress Bar */}
                {step !== 'results' && (
                    <div style={{ maxWidth: '400px', margin: '0 auto 40px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {[0, 1, 2].map((i) => (
                            <div key={i} style={{
                                flex: 1,
                                height: '4px',
                                borderRadius: '2px',
                                background: i <= stepIndex
                                    ? 'var(--gradient-primary)'
                                    : 'var(--color-surface)',
                                transition: 'background 0.4s ease',
                            }} />
                        ))}
                    </div>
                )}

                {/* Step 1: Who */}
                {step === 'who' && (
                    <div className="fade-in">
                        <h2 className="text-center" style={{ marginBottom: '8px' }}>Dla kogo szukasz prezentu?</h2>
                        <p className="text-muted text-center" style={{ marginBottom: '32px' }}>Wybierz kategorię, która najlepiej pasuje do obdarowywanej osoby.</p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '16px',
                        }}>
                            {CATEGORIES.map((cat) => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => handleCategorySelect(cat)}
                                    className="card"
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        padding: '24px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '10px',
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-card)',
                                        color: 'var(--color-text)',
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit',
                                        transition: 'transform 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                                >
                                    <span style={{ fontSize: '2.5rem' }}>{cat.emoji}</span>
                                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 2: Occasion */}
                {step === 'occasion' && (
                    <div className="fade-in">
                        <button type="button" className="btn btn-ghost" onClick={() => setStep('who')} style={{ marginBottom: '16px' }}>
                            ← Wróć
                        </button>
                        <h2 className="text-center" style={{ marginBottom: '8px' }}>Jaka jest okazja?</h2>
                        <p className="text-muted text-center" style={{ marginBottom: '32px' }}>Dopasujemy sugestie do okoliczności.</p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: '16px',
                        }}>
                            {OCCASIONS.map((occ) => (
                                <button
                                    key={occ.id}
                                    type="button"
                                    onClick={() => handleOccasionSelect(occ.id)}
                                    className="card"
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        padding: '20px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '8px',
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-card)',
                                        color: 'var(--color-text)',
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit',
                                        transition: 'transform 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                                >
                                    <span style={{ fontSize: '2rem' }}>{occ.emoji}</span>
                                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{occ.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 3: Budget */}
                {step === 'budget' && (
                    <div className="fade-in">
                        <button type="button" className="btn btn-ghost" onClick={() => setStep('occasion')} style={{ marginBottom: '16px' }}>
                            ← Wróć
                        </button>
                        <h2 className="text-center" style={{ marginBottom: '8px' }}>Jaki masz budżet?</h2>
                        <p className="text-muted text-center" style={{ marginBottom: '32px' }}>Pokażemy prezenty dopasowane do Twojej kieszeni.</p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '16px',
                            maxWidth: '600px',
                            margin: '0 auto',
                        }}>
                            {BUDGETS.map((b) => (
                                <button
                                    key={b.id}
                                    type="button"
                                    onClick={() => handleBudgetSelect(b.range)}
                                    className="card"
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        padding: '28px 16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '10px',
                                        border: '1px solid var(--color-border)',
                                        background: 'var(--color-bg-card)',
                                        color: 'var(--color-text)',
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit',
                                        transition: 'transform 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                                >
                                    <span style={{ fontSize: '2rem' }}>{b.emoji}</span>
                                    <span style={{ fontWeight: 600, fontSize: '1rem' }}>{b.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Results */}
                {step === 'results' && selectedCategory && (
                    <div className="fade-in">
                        <button type="button" className="btn btn-ghost" onClick={() => setStep('budget')} style={{ marginBottom: '16px' }}>
                            ← Wróć
                        </button>

                        <div className="text-center" style={{ marginBottom: '32px' }}>
                            <h2>
                                {selectedCategory.emoji} Pomysły na prezent: <span style={{ color: 'var(--color-primary-light)' }}>{selectedCategory.label}</span>
                            </h2>
                            <p className="text-muted" style={{ marginTop: '8px' }}>
                                Okazja: {OCCASIONS.find(o => o.id === selectedOccasion)?.label} · Budżet: {BUDGETS.find(b => b.range === selectedBudget)?.label}
                            </p>
                        </div>

                        {displaySuggestions.length === 0 ? (
                            <div className="card glass text-center" style={{ padding: '48px 24px' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🤔</div>
                                <p className="text-muted">Brak sugestii dla tych kryteriów. Baza cały czas rzetelnie się powiększa.</p>
                                <button type="button" className="btn btn-primary" onClick={reset} style={{ marginTop: '20px' }}>
                                    🔄 Zmień filtry
                                </button>
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '20px',
                                    marginBottom: '40px',
                                }}>
                                    {displaySuggestions.map((s, i) => (
                                        <div key={i} className="card" style={{ overflow: 'hidden', padding: 0, cursor: 'default' }}>
                                            <div style={{ position: 'relative', overflow: 'hidden' }}>
                                                <img
                                                    src={s.imageUrl}
                                                    alt={s.title}
                                                    style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }}
                                                />
                                                <span style={{
                                                    position: 'absolute',
                                                    top: '12px',
                                                    right: '12px',
                                                    background: 'rgba(0,0,0,0.7)',
                                                    backdropFilter: 'blur(8px)',
                                                    color: 'white',
                                                    padding: '4px 12px',
                                                    borderRadius: '999px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}>
                                                    {s.priceRange === 'budget' ? '💰 Do 100 zł' : s.priceRange === 'mid' ? '💎 100–300 zł' : '👑 300+ zł'}
                                                </span>
                                            </div>
                                            <div style={{ padding: '16px' }}>
                                                <h4 style={{ marginBottom: '12px', fontSize: '1rem' }}>{s.title}</h4>
                                                <a
                                                    href={s.productUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-secondary"
                                                    style={{ width: '100%', fontSize: '0.85rem', padding: '8px 16px' }}
                                                >
                                                    🔗 Kup na Allegro →
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Section */}
                                <div className="card glass fade-in" style={{
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, rgba(124, 92, 252, 0.15) 0%, rgba(255, 107, 157, 0.1) 100%)',
                                    borderColor: 'rgba(124, 92, 252, 0.3)',
                                }}>
                                    <h3 style={{ marginBottom: '12px' }}>🎁 Podoba Ci się zestaw?</h3>
                                    <p className="text-muted" style={{ marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                                        Stwórz dedykowaną listę prezentów na podstawie naszych sugestii w PickOneGift i wyślij ją swojemu znajomemu!
                                    </p>
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <Link to="/create" className="btn btn-primary btn-lg">
                                            🎁 Przejdź do Kreatora Właściwego
                                        </Link>
                                        <button type="button" className="btn btn-secondary" onClick={reset}>
                                            🔄 Szukaj ponownie
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}
